import {
  Button,
  Icon,
  InputLabel,
  Tag,
  Typo,
} from "@pepperstone/pepper-ui-kit";
import React, { useEffect, useRef, useState } from "react";

type DropDownLabels = {
  selectAccountLabel?: string;
  allItemsSelectedLabel: string;
  itemsSelectedLabel: string;
  clearSelectionLabel: string;
  selectAllLabel?: string;
  applyButtonLabel?: string;
};

export type DropdownOption<T = string> = {
  label: string;
  value: T;
};

type DropdownProps = {
  id?: string;
  options: DropdownOption[];
  selected: DropdownOption[];
  dropDownLabels?: DropDownLabels;
  isMobile?: boolean;
  label?: string;
  handleApply: (selected: DropdownOption[]) => void;
};

export const DropDownV2: React.FC<DropdownProps> = ({
  id,
  options,
  selected,
  handleApply,
  dropDownLabels,
  isMobile,
  label,
}) => {
  const [open, setOpen] = useState(false);
  const [localSelectedItems, setLocalSelectedItems] = useState<
    DropdownOption[]
  >([]);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => {
    if (!open) {
      setLocalSelectedItems(selected);
    }
    setOpen(!open);
  };

  const handleCheckboxChange = (option: DropdownOption) => {
    if (localSelectedItems.some((o) => o.value === option.value)) {
      setLocalSelectedItems(
        localSelectedItems.filter((item) => item.value !== option.value),
      );
    } else {
      setLocalSelectedItems([...localSelectedItems, option]);
    }
  };

  const clearSelection = () => {
    setLocalSelectedItems([]);
  };

  const selectAll = () => {
    setLocalSelectedItems(options);
  };

  const getButtonLabel = () => {
    if (selected.length === 0) return dropDownLabels?.selectAccountLabel ?? "";
    if (selected.length === options.length)
      return dropDownLabels?.allItemsSelectedLabel ?? "";
    if (selected.length === 1) return selected[0].label;
    return `${selected.length} ${dropDownLabels?.itemsSelectedLabel}`;
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setLocalSelectedItems(selected);
      setOpen(false);
    }
  };

  const handleApplyClick = () => {
    handleApply(localSelectedItems);
    setOpen(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex w-full flex-col gap-y-2" ref={dropdownRef}>
      {id && label && <InputLabel value={label} htmlFor={id} />}
      <div>
        <Button
          id={id}
          type="button"
          variant="ps-btn-secondary"
          size="ps-btn-md"
          onClick={toggleDropdown}
          className="w-full !bg-cta-background-secondary-base !outline !outline-1 !outline-offset-0 outline-border-primary-medium hover:!bg-cta-background-secondary-base hover:outline-border-blue-base focus:!outline-border-blue-base [&>div]:w-full"
        >
          <div className="flex w-full justify-between">
            <Tag bgColor="black" label={String(getButtonLabel())} />
            <div className="pt-1">
              <Icon
                icon={open ? "arrow-up" : "arrow-down"}
                size="tiny"
                color="black"
              />
            </div>
          </div>
        </Button>
        {open && (
          <div
            className={`absolute ${isMobile ? "w-[calc(100%-34px)]" : "w-max min-w-[260px]"} z-10 mt-2 rounded-md bg-cta-background-secondary-base shadow-lg`}
          >
            <div className="mt-4 max-h-[300px] overflow-y-scroll p-4">
              {options?.map((option) => (
                <label
                  key={option.value}
                  className="mb-4 block min-w-[200px] rounded-md p-2 hover:bg-gray-200"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      value={option.value}
                      checked={localSelectedItems.some(
                        (s) => s.value === option.value,
                      )}
                      onChange={() => handleCheckboxChange(option)}
                      className="me-2 h-5 min-h-[20px] w-5 min-w-[20px] rounded border-gray-300 accent-black"
                    />
                    <Typo elem="p" size="xs" type="subheading">
                      {option.label}
                    </Typo>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between gap-4 p-4 pt-0">
              <div>
                {localSelectedItems.length === 0 ? (
                  <button onClick={selectAll}>
                    <Typo
                      elem="p"
                      size="sm"
                      type="body"
                      textColor="ps-text-text-primary-secondary"
                      responsiveClasses="underline"
                    >
                      {dropDownLabels?.selectAllLabel ?? ""}
                    </Typo>
                  </button>
                ) : (
                  <button onClick={clearSelection}>
                    <Typo
                      elem="p"
                      size="sm"
                      type="body"
                      textColor="ps-text-text-primary-secondary"
                      responsiveClasses="underline"
                    >
                      {dropDownLabels?.clearSelectionLabel ?? ""}
                    </Typo>
                  </button>
                )}
              </div>
              <div>
                <Button
                  type="button"
                  size="ps-btn-sm"
                  className="w-full"
                  variant="ps-btn-primary"
                  disabled={localSelectedItems.length === 0}
                  onClick={handleApplyClick}
                >
                  {dropDownLabels?.applyButtonLabel ?? ""}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
